'use client';

import { useEffect, useState, useRef, RefObject, useCallback } from 'react';
import { Calendar, Mail, Phone, User, MessageSquare, AlertCircle, Check, X, Loader2 } from 'lucide-react';
import { useAppointmentModalStore, AppointmentModalState } from '@/store/useAppointmentModalStore';
import { z } from 'zod';

// Schémas Zod
const step1Schema = z.object({
  name: z.string().min(1, 'Le nom est requis').min(2, 'Le nom doit contenir au moins 2 caractères'),
  email: z.string().min(1, "L'email est requis").email('Veuillez entrer un email valide'),
  phone: z.string().min(1, 'Le téléphone est requis').refine(
    (val) => val.replace(/\D/g, '').length >= 10,
    { message: 'Le numéro doit contenir au moins 10 chiffres' }
  )
});

const step2Schema = z.object({
  datetime: z.string().min(1, "La date et l'heure sont requises")
    .refine((val) => new Date(val) > new Date(), { message: 'Veuillez sélectionner une date future' })
    .refine((val) => {
      const hours = new Date(val).getHours();
      return hours >= 9 && hours < 18;
    }, { message: 'Veuillez choisir un horaire entre 9h et 18h' }),
  comments: z.string().optional(),
  terms: z.boolean().refine((val) => val === true, { message: 'Vous devez accepter les conditions' })
});

// Types
type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type FormErrors = Record<string, string>;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  webhookUrl?: string;
}

// Définition du type pour les composants d'icône
interface IconProps {
  className?: string;
  'aria-hidden'?: boolean;
}

// Hook pour détecter les clics en dehors du composant
function useClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: () => void
): void {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) return;
      handler();
    };
    
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

// Fonction pour formater les numéros de téléphone
function formatPhoneNumber(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');
  let formatted = '';
  
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && i % 2 === 0) formatted += ' ';
    formatted += digits[i];
  }
  
  return formatted;
}

/**
 * Composant principal pour le modal de prise de rendez-vous
 */
const ModalContent: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  description, 
  webhookUrl = '/api/send-appointment'
}) => {
  // Refs
  const modalRef = useRef<HTMLDivElement | null>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);
  
  // États généraux
  const [isMounted, setIsMounted] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [attemptedStep2Submit, setAttemptedStep2Submit] = useState(false);
  const totalSteps = 2;
  
  // Récupérer les données initiales du store
  const initialStoreData = useAppointmentModalStore((state: AppointmentModalState) => state.initialData);
  
  // États de l'étape 1
  const [step1Data, setStep1Data] = useState<Step1Data>({
    name: initialStoreData?.name || '',
    email: initialStoreData?.email || '',
    phone: initialStoreData?.phone || ''
  });
  const [step1Errors, setStep1Errors] = useState<FormErrors>({});
  const [step1Touched, setStep1Touched] = useState({name: false, email: false, phone: false});
  
  // États de l'étape 2
  const [step2Data, setStep2Data] = useState<Step2Data>({
    datetime: initialStoreData?.datetime || '',
    comments: initialStoreData?.comments || '',
    terms: false
  });
  const [step2Errors, setStep2Errors] = useState<FormErrors>({});
  // Cette variable n'est pas utilisée mais peut être conservée pour une future fonctionnalité
  // const [step2Touched, setStep2Touched] = useState({datetime: false, terms: false});
  
  // Fonction pour déterminer si les erreurs de l'étape 2 doivent être affichées
  const shouldShowStep2Errors = () => step === 2 && attemptedStep2Submit === true;

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setStep1Data({name: '', email: '', phone: ''});
    setStep1Errors({});
    setStep1Touched({name: false, email: false, phone: false});
    
    setStep2Data({datetime: '', comments: '', terms: false});
    setStep2Errors({});
    // setStep2Touched({datetime: false, terms: false});
    
    setStep(1);
    setStatus('idle');
    setLoading(false);
    setAttemptedStep2Submit(false);
  };
  
  // Demander confirmation avant fermeture
  // const handleCloseWithConfirmation = () => {
  //   const hasStartedFilling = 
  //     step1Data.name || 
  //     step1Data.email || 
  //     step1Data.phone || 
  //     step2Data.datetime || 
  //     step2Data.comments;
    
  //   if (hasStartedFilling && status !== 'success') {
  //     setShowConfirmDialog(true);
  //   } else {
  //     onClose();
  //   }
  // };

  const handleCloseWithConfirmation = useCallback(() => {
    const hasStartedFilling = 
      step1Data.name || 
      step1Data.email || 
      step1Data.phone || 
      step2Data.datetime || 
      step2Data.comments;
    
    if (hasStartedFilling && status !== 'success') {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  }, [step1Data, step2Data, status, onClose]);
  
  // Effet: Touche Échap
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleCloseWithConfirmation();
    };
    
    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, handleCloseWithConfirmation]);
  
  // Effet: Focus management
  useEffect(() => {
    if (isOpen && isMounted && initialFocusRef.current) {
      setTimeout(() => initialFocusRef.current?.focus(), 50);
    }
  }, [isOpen, isMounted, step]);
  
  // Effet: Body scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);
  
  // Effet: Montage initial
  useEffect(() => { setIsMounted(true); }, []);
  
  // Effet: Réinitialisation à la fermeture
  useEffect(() => {
    if (!isOpen) setTimeout(resetForm, 300);
  }, [isOpen]);
  
  // Effet: Mise à jour avec les données initiales
  useEffect(() => {
    if (isOpen && initialStoreData) {
      setStep1Data({
        name: initialStoreData.name || '',
        email: initialStoreData.email || '',
        phone: initialStoreData.phone || ''
      });
      
      setStep2Data(prev => ({
        ...prev,
        datetime: initialStoreData.datetime || prev.datetime,
        comments: initialStoreData.comments || prev.comments
      }));
    }
  }, [isOpen, initialStoreData]);

  // Effet: Réinitialisation des erreurs lors du changement d'étape
  useEffect(() => {
    if (step === 2) {
      setStep2Errors({});
      setAttemptedStep2Submit(false);
    }
  }, [step]);
  
  // Setup click outside handler
  useClickOutside(modalRef, () => {
    if (isOpen) handleCloseWithConfirmation();
  });
  
  // Ne rien rendre si non monté ou fermé
  if (!isMounted || !isOpen) return null;

  const confirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
    resetForm();
  };
  
  const cancelClose = () => {
    setShowConfirmDialog(false);
  };
  
  // Gestion des changements de l'étape 1
  const handleStep1Change = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    const processedValue = name === 'phone' ? formatPhoneNumber(value) : value;
    
    // Mettre à jour les données
    setStep1Data(prev => ({ ...prev, [name]: processedValue }));
    
    // Valider le champ
    try {
      const fieldSchema = z.object({ [name]: step1Schema.shape[name as keyof Step1Data] });
      fieldSchema.parse({ [name]: processedValue });
      
      // Si validation réussie, supprimer l'erreur
      setStep1Errors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        setStep1Errors(prev => ({ ...prev, [name]: error.errors[0].message }));
      }
    }
    
    // Marquer comme touché
    setStep1Touched(prev => ({ ...prev, [name]: true }));
  };
  
  // Gestion des changements de l'étape 2
  const handleStep2Change = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = event.target;
    const newValue = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;

    // Mettre à jour les données
    setStep2Data(prev => ({ ...prev, [name]: newValue }));

    // Ne valider que si l'utilisateur a déjà tenté de soumettre
    if (attemptedStep2Submit) {
      try {
        if (name === 'datetime' || name === 'terms') {
          const fieldSchema = z.object({ [name]: step2Schema.shape[name as 'datetime' | 'terms' | 'comments'] });
          fieldSchema.parse({ [name]: newValue });
          
          // Si validation réussie, supprimer l'erreur
          setStep2Errors(prev => {
            const newErrors = {...prev};
            delete newErrors[name];
            return newErrors;
          });
        }
      } catch (error) {
        if (error instanceof z.ZodError) {
          setStep2Errors(prev => ({ ...prev, [name]: error.errors[0].message }));
        }
      }
    }
    
    // Marquer comme touché
    // setStep2Touched(prev => ({ ...prev, [name as 'datetime' | 'terms']: true }));
  };

  // Passage à l'étape suivante
  const handleNextStep = (event: React.MouseEvent) => {
    event.preventDefault();

    try {
      // Valider l'étape 1
      step1Schema.parse(step1Data);
      
      // Si validation réussie, passer à l'étape 2
      setStep(2);
      setStep2Errors({});
      setAttemptedStep2Submit(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Traiter les erreurs
        const newErrors: FormErrors = {};
        error.errors.forEach(err => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        
        setStep1Errors(newErrors);
        
        // Marquer tous les champs comme touchés
        setStep1Touched({name: true, email: true, phone: true});
      }
    }
  };

  // Retour à l'étape précédente
  const handlePrevStep = () => {
    setStep(1);
  };

  // Soumission du formulaire
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Ne soumettre que si on est à l'étape 2
    if (step !== 2) return;
    if (status === 'submitting') return;
    
    // Marquer comme ayant tenté de soumettre
    setAttemptedStep2Submit(true);
    
    try {
      // Valider l'étape 2
      step2Schema.parse(step2Data);
      
      // Si validation réussie, soumettre
      setStatus('submitting');
      setLoading(true);
      
      // Combiner les données
      const formData = { ...step1Data, ...step2Data };
      
      // Format pour la soumission
      const formattedData = {
        ...formData,
        formattedDate: new Date(formData.datetime).toLocaleString('fr-FR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      
      // Appel API
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error: ${response.status}`);
      }

      // Succès
      setStatus('success');
      setTimeout(() => onClose(), 3000);
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Erreurs de validation
        const newErrors: FormErrors = {};
        error.errors.forEach(err => {
          const field = err.path[0] as string;
          newErrors[field] = err.message;
        });
        
        setStep2Errors(newErrors);
        // setStep2Touched({datetime: true, terms: true});
      } else {
        // Erreur de soumission
        console.error('Erreur lors de la soumission:', error);
        setStep2Errors(prev => ({
          ...prev,
          submit: 'Une erreur est survenue. Veuillez réessayer.'
        }));
      }
      
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Date minimale (demain)
  const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow.toISOString().slice(0, 16);
  };
  
  // Composant réutilisable pour les champs de l'étape 1
  const renderStep1Field = (
    name: keyof Step1Data,
    label: string,
    type: string,
    Icon: React.ComponentType<IconProps>,
    placeholder: string = '',
    autoFocus: boolean = false
  ) => {
    const hasError = step1Errors[name] && step1Touched[name];
    
    return (
      <div>
        <label htmlFor={`modal-${name}`} className="block text-sm font-medium text-gray-700 mb-1">
          {label} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" aria-hidden={true} />
          <input
            id={`modal-${name}`}
            type={type}
            name={name}
            value={step1Data[name]}
            onChange={handleStep1Change}
            className={`pl-10 border p-2 w-full rounded mt-1 focus:ring-2 focus:ring-purple-500 ${
              hasError ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={placeholder}
            required
            ref={autoFocus ? initialFocusRef : undefined}
            aria-invalid={hasError ? 'true' : 'false'}
            aria-describedby={hasError ? `error-${name}` : undefined}
          />
        </div>
        {hasError && (
          <div 
            id={`error-${name}`} 
            className="flex items-center gap-1 text-red-500 text-sm mt-1" 
            role="alert"
          >
            <AlertCircle className="h-4 w-4" aria-hidden={true} />
            <span>{step1Errors[name]}</span>
          </div>
        )}
      </div>
    );
  };
  
  // Rendu de l'état de succès
  if (status === 'success') {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity" aria-hidden={true} />
        <div 
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-8 rounded-lg shadow-xl w-full max-w-md z-50 transition-all"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-success-title"
          ref={modalRef}
        >
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <Check className="h-6 w-6 text-green-600" aria-hidden={true} />
            </div>
            <h2 id="modal-success-title" className="text-2xl font-bold text-gray-900 mb-2">Rendez-vous confirmé !</h2>
            <p className="text-gray-600 mb-6">
              Votre demande a été envoyée avec succès. Vous recevrez bientôt un email de confirmation.
            </p>
            <button
              onClick={onClose}
              className="w-full bg-primary text-white px-4 py-2 rounded-full hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              Fermer
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity" aria-hidden={true} />
      
      {/* Modal */}
      <div 
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-8 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto z-50 transition-all"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={modalRef}
      >
        {/* Title */}
        <h2 id="modal-title" className="text-2xl font-bold mb-4">{title}</h2>
        
        {/* Close button */}
        <button
          onClick={handleCloseWithConfirmation}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
          type="button"
        >
          <X className="h-6 w-6" />
        </button>
        
        {/* Progress bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / totalSteps) * 100}%` }}
              role="progressbar"
              aria-valuenow={(step / totalSteps) * 100}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div className="text-sm text-gray-600 mt-2">
            Étape {step} sur {totalSteps}
          </div>
        </div>

        {/* Description */}
        <p className="mb-6 text-gray-600">{description}</p>

        {/* Error message général */}
        {step2Errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 flex items-center gap-2" role="alert">
            <AlertCircle className="h-5 w-5 flex-shrink-0" aria-hidden={true} />
            <span>{step2Errors.submit}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {step === 1 ? (
            <>
              {/* Step 1: Personal Information */}
              <h3 className="text-lg font-medium text-gray-800 mb-4">Informations personnelles</h3>
              {renderStep1Field('name', 'Nom complet', 'text', User, 'Jean Dupont', true)}
              {renderStep1Field('email', 'Email', 'email', Mail, 'jean@exemple.fr')}
              {renderStep1Field('phone', 'Téléphone', 'tel', Phone, '06 12 34 56 78')}
            </>
          ) : (
            <>
              {/* Step 2: Appointment Details */}
              <h3 className="text-lg font-medium text-gray-800 mb-4">Détails du rendez-vous</h3>
              
              {/* Date and time field */}
              <div>
                <label htmlFor="modal-datetime" className="block text-sm font-medium text-gray-700 mb-1">
                  Date et heure <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" aria-hidden={true} />
                  <input
                    id="modal-datetime"
                    type="datetime-local"
                    name="datetime"
                    value={step2Data.datetime}
                    onChange={handleStep2Change}
                    className={`pl-10 border p-2 w-full rounded mt-1 focus:ring-2 focus:ring-purple-500 ${
                      shouldShowStep2Errors() && step2Errors.datetime ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                    ref={initialFocusRef}
                    min={getTomorrow()}
                    aria-invalid={shouldShowStep2Errors() && step2Errors.datetime ? 'true' : 'false'}
                    aria-describedby={shouldShowStep2Errors() && step2Errors.datetime ? 'error-datetime' : undefined}
                  />
                </div>
                {shouldShowStep2Errors() && step2Errors.datetime && (
                  <div id="error-datetime" className="flex items-center gap-1 text-red-500 text-sm mt-1" role="alert">
                    <AlertCircle className="h-4 w-4" aria-hidden={true} />
                    <span>{step2Errors.datetime}</span>
                  </div>
                )}
              </div>
              
              {/* Comments field */}
              <div>
                <label htmlFor="modal-comments" className="block text-sm font-medium text-gray-700 mb-1">
                  Commentaires
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 text-gray-400 h-5 w-5" aria-hidden={true} />
                  <textarea
                    id="modal-comments"
                    name="comments"
                    value={step2Data.comments}
                    onChange={handleStep2Change}
                    className="pl-10 border p-2 w-full rounded mt-1 focus:ring-2 focus:ring-purple-500 border-gray-300"
                    placeholder="Informations complémentaires..."
                    rows={3}
                  />
                </div>
              </div>
              
              {/* Terms checkbox */}
              <div className="flex items-start mt-4">
                <input
                  id="modal-terms"
                  type="checkbox"
                  name="terms"
                  checked={step2Data.terms}
                  onChange={handleStep2Change}
                  className={`mt-1 h-4 w-4 ${
                    shouldShowStep2Errors() && step2Errors.terms ? 'border-red-500 ring-1 ring-red-500' : ''
                  }`}
                  required
                  aria-invalid={shouldShowStep2Errors() && step2Errors.terms ? 'true' : 'false'}
                  aria-describedby={shouldShowStep2Errors() && step2Errors.terms ? 'error-terms' : undefined}
                />
                <label htmlFor="modal-terms" className="ml-2 text-sm text-gray-600">
                  J&apos;accepte les conditions générales <span className="text-red-500">*</span>
                </label>
              </div>
              {shouldShowStep2Errors() && step2Errors.terms && (
                <div id="error-terms" className="flex items-center gap-1 text-red-500 text-sm mt-1" role="alert">
                  <AlertCircle className="h-4 w-4" aria-hidden={true} />
                  <span>{step2Errors.terms}</span>
                </div>
              )}
            </>
          )}

          {/* Navigation buttons */}
          <div className="mt-6 flex justify-between gap-4">
            {step > 1 && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="px-4 py-2 border rounded-full text-gray-600 hover:bg-gray-50 w-1/2 transition-colors"
              >
                Retour
              </button>
            )}
            
            {step < totalSteps ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="bg-primary text-white px-4 py-2 rounded-full hover:bg-primary-600 w-full ml-auto transition-colors"
              >
                Suivant
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || status === 'submitting'}
                className="bg-primary text-white px-4 py-2 rounded-full hover:bg-primary-600 w-full ml-auto disabled:bg-gray-400 transition-colors flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" aria-hidden={true} />
                    Envoi en cours...
                  </>
                ) : (
                  'Confirmer'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
      
      {/* Dialogue de confirmation de fermeture */}
      {showConfirmDialog && (
        <>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-60 transition-opacity" aria-hidden={true} />
          <div 
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-xl w-full max-w-sm z-60 transition-all"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby="confirm-dialog-desc"
          >
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 mb-4">
                <AlertCircle className="h-6 w-6 text-amber-600" aria-hidden={true} />
              </div>
              <h3 id="confirm-dialog-title" className="text-xl font-semibold text-gray-900 mb-2">
                Êtes-vous sûr ?
              </h3>
              <p id="confirm-dialog-desc" className="text-gray-600 mb-6">
                Vous avez commencé à remplir le formulaire. Si vous fermez maintenant, toutes vos données seront perdues.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={cancelClose}
                  className="w-1/2 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmClose}
                  className="w-1/2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  Quitter
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ModalContent;